import { Component, ContentChild, ElementRef, EventEmitter, Input, Output, TemplateRef, ViewChild, inject } from "@angular/core";
import { UntypedFormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";

import { of, throwError } from "rxjs";
import { catchError, finalize, first, tap } from "rxjs/operators";

import { DffRouteParams } from "@dffedb/router";
import { DffError } from "@dffedb/util";
import { LoginService } from "@e-forsyning/common/auth";
import { RedirectService } from "@e-forsyning/common/router";

import { LoginData } from "../login-data.model";
import { LoginHjaelpService } from "../login-hjaelp/login-hjaelp.service";
import { MatExpansionPanel } from "@angular/material/expansion";
import { MatFormField, MatError } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatButton } from "@angular/material/button";
import { MatomoTracker } from "ngx-matomo-client";
import { BiometricService } from "../biometrics/biometrics.service";
import { CommonModule } from "@angular/common";

@Component({
    selector: "login-email",
    templateUrl: "./login-email.component.html",
    imports: [FormsModule, ReactiveFormsModule, MatExpansionPanel, MatFormField, MatInput, MatError, MatCheckbox, MatButton, CommonModule]
})
export class LoginEmailComponent {
    @Input()
    public parent: UntypedFormGroup;

    @Input()
    public fejlBesked = "";

    @Output()
    public loginSucceeded = new EventEmitter<any>();

    @ViewChild("emailInput", { static: true }) public emailInput: ElementRef;

    @ContentChild("biometricLoginButton") biometricTemplate?: TemplateRef<any>;

    public foersteGangAktiveret = false;
    public glemtKodeordAktiveret = false;
    public arbejder = false;
    public infoBesked = "";
    public visFeltFejl = false;

    private readonly matomoTracker = inject(MatomoTracker);

    constructor(
        private readonly loginService: LoginService,
        private readonly loginHjaelpService: LoginHjaelpService,
        private readonly redirectService: RedirectService,
        private readonly routeParams: DffRouteParams,
        private readonly biometricService: BiometricService
    ) {}

    public focus(): void {
        this.emailInput.nativeElement.focus();
    }

    public loginClick(): void {
        if (this.foersteGangAktiveret && this.parent.valid) {
            this.firstEmailLoginClick();
        } else if (this.glemtKodeordAktiveret) {
            this.sendGlemtKodeordClick();
        } else if (this.foersteGangAktiveret === false) {
            const loginData: LoginData = {
                brugernavn: this.parent.get("emailGroup.email").value,
                kodeord: this.parent.get("emailGroup.adgangskode").value,
                huskMig: this.parent.get("huskMig").value
            };
            this.forbrugerLogin(loginData);
        } else {
            this.visFeltFejl = true;
        }
    }

    public forbrugerLogin(credentials: LoginData): void {
        this.fejlBesked = "";
        this.arbejder = true;
        this.loginService
            .login(credentials.brugernavn, credentials.kodeord, credentials.huskMig)
            .pipe(
                first(),
                tap(async (result) => {
                    const isTechnicianLogin = !credentials.brugernavn.includes("@") && isNaN(parseInt(credentials.brugernavn, 10));
                    const available = await this.biometricService.isAvailable();
                    if (result.success) {
                        if (available && !localStorage.getItem("biometricAsked")) {
                            try {
                                await this.biometricService.enableBiometricLogin(
                                    credentials.brugernavn,
                                    credentials.kodeord,
                                    "Aktiver biometrisk login",
                                    "Aktiver biometrisk login",
                                    "Bekræft din identitet for at gemme dine loginoplysninger sikkert. Herefter kan du logge ind hurtigere med finger- eller ansigtsgenkendelse."
                                );
                                localStorage.setItem("biometricAsked", "true");
                                this.matomoTracker.trackEvent("Authentication", "Login Success", isTechnicianLogin ? "Technician" : "Email");
                                this.loginSucceeded.emit();
                            } catch (err) {
                                localStorage.setItem("biometricAsked", "true");
                                this.loginSucceeded.emit();
                            }
                            this.matomoTracker.trackEvent("Authentication", "Login Success", isTechnicianLogin ? "Technician" : "Email");
                        } else {
                            this.matomoTracker.trackEvent("Authentication", "Login Success", isTechnicianLogin ? "Technician" : "Email");
                            this.loginSucceeded.emit();
                        }
                    } else {
                        if (isTechnicianLogin) {
                            this.matomoTracker.trackEvent("Authentication", "Technician Login", "Failed");
                        } else {
                            this.matomoTracker.trackEvent("Authentication", "Login Failed", "Email - Invalid Credentials");
                        }
                        this.fejlBesked = "Ugyldig e-mail eller adgangskode";
                    }
                }),
                catchError((error: DffError) => {
                    this.matomoTracker.trackEvent("Authentication", "Login Failed", "Email - Error");
                    this.fejlBesked = error.friendlyMessage || error.message;
                    return throwError(error);
                }),
                finalize(() => {
                    this.arbejder = false;
                })
            )
            .subscribe();
    }

    public firstEmailLoginClick(): void {
        const email = this.parent.get("emailGroup.email").value;
        const adgangskode = this.parent.get("emailGroup.adgangskode").value;
        const forbrugerNr = this.parent.get("forbrugerNrGroup.forbrugerNr").value;
        const kode = this.parent.get("forbrugerNrGroup.kode").value;
        this.arbejder = true;
        this.loginService
            .tilknytEmail(forbrugerNr, kode, email, adgangskode)
            .pipe(
                first(),
                tap(() => {
                    this.matomoTracker.trackEvent("Authentication", "Email Setup", "Success");
                    this.fejlBesked = "";
                    this.foersteGangAktiveret = false;
                }),
                catchError((error: any) => {
                    console.log("FEJL VED LOGIN:::", error.error);
                    this.matomoTracker.trackEvent("Authentication", "Email Setup", "Failed");
                    this.fejlBesked = error.error;
                    return of(null);
                }),
                finalize(() => {
                    this.arbejder = false;
                })
            )
            .subscribe();
    }

    public sendGlemtKodeordClick(): void {
        const email = this.parent.get("emailGroup.email").value;
        this.arbejder = true;
        this.loginService
            .glemtKodeord(email)
            .pipe(
                first(),
                tap(() => {
                    this.matomoTracker.trackEvent("Authentication", "Forgot Password", "Success");
                    this.foersteGangAktiveret = false;
                    this.fejlBesked = "";
                    this.infoBesked = `E-mail sendt til: ${email}`;
                }),
                catchError((error: any) => {
                    this.matomoTracker.trackEvent("Authentication", "Forgot Password", "Failed");
                    console.log("FEJL :::", error.error);
                    this.matomoTracker.trackEvent("Authentication", "Forgot Password", "Failed");
                    this.fejlBesked = error.error;
                    return of(null);
                }),
                finalize(() => {
                    this.arbejder = false;
                })
            )
            .subscribe();
    }

    public onGlemtKodeordClick(): void {
        this.glemtKodeordAktiveret = !this.glemtKodeordAktiveret;
        this.fejlBesked = "";
        this.foersteGangAktiveret = false;
        this.infoBesked = "";
    }

    public firstTimeClick(): void {
        this.fejlBesked = "";
        this.foersteGangAktiveret = !this.foersteGangAktiveret;
    }

    public loginHjaelpClick(): void {
        this.loginHjaelpService.visLoginHjaelpDialog();
    }

    public redirectEfterLogin(): void {
        const path = this.routeParams.get("path");
        if (path && path !== "404") {
            this.redirectService.redirect(path);
        } else {
            this.redirectService.redirectTilInstallationer();
        }
    }
}
